import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ChevronRight, Clock } from "lucide-react";
import api from "@/lib/api";
import { fmtNaira } from "@/lib/format";
import { styleFor } from "@/lib/askThemes";
import { Skeleton } from "@/components/ui/skeleton";

const TABS = [
  { id: "active", label: "Active" },
  { id: "pending", label: "Pending" },
  { id: "completed", label: "Completed" },
  { id: "closed", label: "Closed" },
];

function Choice({ kind, img, title, line, testId }) {
  const navigate = useNavigate();
  return (
    <button
      data-testid={testId}
      onClick={() => navigate(`/asks/new?kind=${kind}`)}
      className={`group relative flex flex-col items-start overflow-hidden rounded-3xl p-4 pt-3 text-left transition-transform active:scale-[0.97] ask-pop ${
        kind === "gift" ? "bg-gradient-to-br from-emerald-50 to-teal-100 border border-emerald-200/70" : "bg-gradient-to-br from-amber-50 to-orange-100 border border-amber-200/70"
      }`}
    >
      <img src={img} alt="" className="h-24 w-24 object-contain self-center transition-transform group-hover:scale-105" />
      <p className="mt-1 font-display text-lg font-extrabold text-foreground">{title}</p>
      <p className="mt-0.5 text-xs leading-snug text-muted-foreground">{line}</p>
    </button>
  );
}

function AskRow({ a }) {
  const navigate = useNavigate();
  const st = styleFor(a);
  const lendState = { pending: "Awaiting reply", countered: "New terms proposed", accepted: "Accepted · awaiting transfer", sent: "Confirm you received it", recorded: "Recorded as debt", declined: "Declined" }[a.status];
  return (
    <button data-testid={`my-ask-${a.id}`} onClick={() => navigate(`/asks/${a.id}`)} className="w-full flex items-center gap-3 rounded-2xl border border-border bg-card p-3 text-left transition-colors hover:bg-secondary/60">
      <span className="h-11 w-11 shrink-0 rounded-2xl flex items-center justify-center text-2xl" style={{ background: st.bg }}>{st.emoji}</span>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-semibold">{a.title || (a.kind === "lend" ? "Lend Me" : "Gift Me")}</p>
        <p className="truncate text-xs text-muted-foreground">
          {a.kind === "lend" ? `${fmtNaira(a.lend.amount, a.currency)} · ${lendState}` :
            a.target ? `${fmtNaira(a.confirmed_total, a.currency)} of ${fmtNaira(a.target, a.currency)}` : `${fmtNaira(a.confirmed_total, a.currency)} received`}
        </p>
      </div>
      {a.pending_count > 0 && (
        <span className="flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-bold text-amber-800"><Clock className="h-3 w-3" />{a.pending_count}</span>
      )}
      <ChevronRight className="h-4 w-4 text-muted-foreground" />
    </button>
  );
}

export default function AskHome() {
  const [tab, setTab] = useState("active");
  const { data, isLoading } = useQuery({ queryKey: ["asks"], queryFn: async () => (await api.get("/asks")).data.asks });
  const list = (data || []).filter((a) => a.tab === tab);
  return (
    <div data-testid="ask-home" className="px-5 pt-6">
      <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-accent">Debii Ask</p>
      <h1 className="mt-1 font-display text-3xl font-extrabold tracking-tight">Ask with zero awkwardness</h1>
      <div className="mt-5 grid grid-cols-2 gap-3">
        <Choice kind="gift" img="/ask/gift.webp" title="🎁 Gift Me" line="No repayment. Just good vibes." testId="gift-me-option-button" />
        <Choice kind="lend" img="/ask/lend.webp" title="🤝 Lend Me" line="Borrow now. Repay later." testId="lend-me-option-button" />
      </div>

      <h2 className="mt-8 font-display text-lg font-bold">My Asks</h2>
      <div className="mt-3 flex gap-1.5 overflow-x-auto no-scrollbar">
        {TABS.map((t) => {
          const n = (data || []).filter((a) => a.tab === t.id).length;
          return (
            <button key={t.id} data-testid={`my-asks-tab-${t.id}`} onClick={() => setTab(t.id)}
              className={`shrink-0 rounded-full px-3.5 py-1.5 text-xs font-semibold transition-colors ${tab === t.id ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground"}`}>
              {t.label}{n ? ` · ${n}` : ""}
            </button>
          );
        })}
      </div>
      <div className="mt-3 space-y-2">
        {isLoading ? [1, 2].map((i) => <Skeleton key={i} className="h-16 rounded-2xl" />) :
          list.length ? list.map((a) => <AskRow key={a.id} a={a} />) :
            <p data-testid="my-asks-empty" className="rounded-2xl border border-dashed border-border p-6 text-center text-sm text-muted-foreground">Nothing here yet.</p>}
      </div>
    </div>
  );
}
